<template>
  <div>
    <!-- Keywords Table View -->
    <div v-if="currentStep === 'keywords_table'">
      <div class="gridMargin">
        <div class="model-sections">
          <div class="table-header sectionPadding cust-brdr" style="margin: 0px;">
            <div class="table-col-keyword">
              <span class="vtext-subtitle th_keywords">Compliance keyword</span>
              <HelpTooltip class="th_keywords_tooltip" :iconStyle="{'padding-top': '3px'}">
                Compliance keywords allow the system to automatically detect messages, capture Opt-in and Opt-out requests from users and respond to them.
              </HelpTooltip>
            </div>
            <div class="table-col-type text-center">
              <span class="vtext-subtitle">Type</span>
            </div>
            <div class="table-col-actions text-end">
              <span class="vtext-subtitle">Actions</span>
            </div>
          </div>
          <div v-for="(thisRule, index) in displayRules" :key="index" class="table-row sectionPadding cust-brdr">
            <div class="table-col-keyword">
              <div v-if="thisRule.keywords?.length">
                <span class="bot-trigger-chip" v-for="thisKeyword in thisRule.keywords" :key="thisKeyword">
                  {{ thisKeyword }}
                </span>
              </div>
              <div v-else>
                <p class="vtext-subtitle" v-if="thisRule.type === 'Opt-in'">No Opt-in keywords added</p>
                <p class="vtext-subtitle" v-else>No Opt-out keywords added</p>
              </div>
            </div>
            <div class="table-col-type text-center">
              <p class="vtext-body">{{ thisRule.type }}</p>
            </div>
            <div class="table-col-actions text-end">
              <span class="tooltip_container">
                <VIcon name="edit-line" class="cursorPointer" @click="editComplianceKeyword(thisRule)"></VIcon>
                <span class="tooltip_text left">Edit</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Keywords View -->
    <div v-if="currentStep === 'editComplianceKeyword'">
      <div class="toolbar-header">
        <VIcon @click="() => currentStep = 'keywords_table'" :size="-4" name="arrow-left-line" class="cursorPointer"></VIcon>
        <h2 class="vtext-card-title--light">Edit compliance keywords</h2>
        <VButton class="vvd-btn-primary save-btn" connotation="cta" appearance="filled" label="Save" @click="saveComplianceKeywords"></VButton>
      </div>

      <div class="gridMargin">
        <div class="vrow">
          <div class="vcol vcol--no-padding vcol-xl-8 vcol-lg-8 vcol-md-8 vcol-sm-12 vcol-xs-12 custPaddingRight">
            <!-- Keywords Accordion -->
            <div class="accordion-container vmb-20">
              <div class="accordion-header cursorPointer" @click="accordionGroupChange('keywords')">
                <span class="vtext-card-title--light">Compliance keywords ({{ selectedRule.type }})</span>
                <VIcon :name="selectedAccordion === 'keywords' ? 'chevron-up-line' : 'chevron-down-line'" :size="-5"></VIcon>
              </div>
              <div v-if="selectedAccordion === 'keywords'" class="accordion-content">
                <div v-if="selectedRule.keywords?.length > 0">
                  <div class="keyword-table">
                    <div class="keyword-table-header sectionPadding cust-brdr">
                      <div class="keyword-col">
                        <span class="vtext-subtitle th_keywords">Compliance keyword</span>
                        <HelpTooltip class="th_keywords_tooltip">
                          Compliance keywords and bot trigger words must remain distinct. A keyword designated for compliance cannot be used as a bot trigger and vice versa.
                        </HelpTooltip>
                      </div>
                      <div class="action-col text-end">
                        <span class="vtext-subtitle">Action</span>
                      </div>
                    </div>
                    <div v-for="(keyword, index) in selectedRule.keywords" :key="index" class="keyword-table-row sectionPadding cust-brdr">
                      <div class="keyword-col">
                        <p class="vtext-body">{{ keyword }}</p>
                      </div>
                      <div class="action-col text-end">
                        <span class="tooltip_container">
                          <VIcon name="delete-line" connotation="alert" class="cursorPointer" @click="deleteKeyword(keyword)"></VIcon>
                          <span class="tooltip_text left">Delete</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="sectionPadding">
                  <div class="infoBanner vmb-20">
                    <div class="text">
                      <b>Note:</b> Emojis, symbols, and special characters such as comma (,) and hash (#) are not supported.
                    </div>
                  </div>

                  <div class="flex-start width100P gap10P">
                    <VTextField v-model="newKeyword" :placeholder="selectedRule.type === 'Opt-in' ? 'Enter keyword (E.g. start, subscribe, resubscribe, opt in)' : 'Enter keyword (E.g. stop, unsubscribe, cancel, opt out)'" :maxlength="20" aria-label="new keyword" appearance="ghost" class="vivid-input flex100P" @keyup.enter="addNewKeyword()"></VTextField>
                    <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Add" :disabled="!newKeyword.length" @click="addNewKeyword()"></VButton>
                  </div>
                </div>
              </div>
            </div>

            <!-- Message Accordion -->
            <div class="accordion-container">
              <div class="accordion-header cursorPointer" @click="accordionGroupChange('message')">
                <span class="vtext-card-title--light">Message</span>
                <VIcon :name="selectedAccordion === 'message' ? 'chevron-up-line' : 'chevron-down-line'" :size="-5"></VIcon>
              </div>
              <div v-if="selectedAccordion === 'message'" class="accordion-content">
                <div class="messagePadding">
                  <div class="infoBanner vmt-20">
                    <VIcon name="info-line" :size="-5" connotation="cta" class="primary icon"></VIcon>
                    <div class="column">
                      <div class="text">
                        Set the message your customers will receive when they {{ selectedRule.type }}.
                      </div>
                    </div>
                  </div>

                  <!-- Slot for channel-specific message content -->
                  <slot name="message-content"></slot>
                </div>
              </div>
            </div>
          </div>

          <!-- Message Preview -->
          <div class="vcol vcol--no-padding vcol-xl-4 vcol-lg-4 vcol-md-4 vcol-sm-12 vcol-xs-12 model-sections previewContainer">
            <div class="previewHeader">
              <p class="vtext-card-title">Preview</p>
            </div>
            <div class="sectionPadding">
              <!-- Whatsapp Text Message preview -->
              <WhatsAppPreview 
                v-if="channel === 'whatsapp' && messageType === 'text' && textMessage" 
                origin="compliance-keywords" 
                selectedLanguage="en"
                :body="{ body: { text: textMessage } }" 
                :templateLoading="false"
                templateType="generic"
                :isViewOnlyMode="false"
                :isDuplicateBroadcast="false"
                finalUrlType="static"
              />
              <!-- Whatsapp Message Template preview -->
              <WhatsAppPreview 
                v-else-if="channel === 'whatsapp' && messageType === 'whatsappTemplate' && (previewData?.body?.text || previewData?.header || selectedTemplate?.id)" 
                origin="compliance-keywords" 
                :selectedLanguage="selectedTemplate?.language || 'en'"
                :body="previewData" 
                :templateLoading="templatesLoading"
                :templateType="selectedTemplate?.template_subtype || 'generic'"
                :carouselMessage="selectedTemplate?.message"
                :isViewOnlyMode="false"
                :isDuplicateBroadcast="false"
                :finalUrlType="finalUrlType"
              />
              <!-- WhatsApp Quick Replies preview -->
              <div v-else-if="channel === 'whatsapp' && messageType === 'quickReplies'">
                <WhatsAppPreview
                  v-if="quickReplyMessage"
                  origin="compliance-keywords"
                  selectedLanguage="en"
                  :body="{ body: { text: quickReplyMessage } }"
                  :quickReplyButtons="quickReplyButtons"
                  :templateLoading="false"
                  templateType="generic"
                  :isViewOnlyMode="false"
                  :isDuplicateBroadcast="false"
                  finalUrlType="static"
                />
                <div v-if="!quickReplyMessage" class="no-preview-state">
                  <div class="preview-placeholder">
                    <p class="vtext-subtitle">Configure your message to see preview</p>
                  </div>
                </div>
              </div>
              <div v-else-if="channel === 'whatsapp'" class="no-preview-state">
                <div class="preview-placeholder">
                  <VIcon name="message-line" class="preview-placeholder-icon"></VIcon>
                  <p class="vtext-subtitle">Configure your message to see preview</p>
                </div>
              </div>
              <!-- SMS and RCS message Template Preview -->
              <JsonBuilderPreview 
                v-else
                :adMessageJsonElements="selectedAdMessageJSON" 
                :carouselMessage="carouselMessage"
                :selectedChannel="channel"
                :finalUrlType="'static'"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HelpTooltip from "./HelpTooltip.vue";
import JsonBuilderPreview from "@/pages/broadcast/json-builder-preview.vue";
import WhatsAppPreview from "@/pages/broadcast/whats-app-preview.vue";
import { useUIStore } from '@/stores/ui';

export default {
  name: 'ComplianceKeywords',
  components: { HelpTooltip, JsonBuilderPreview, WhatsAppPreview },
  props: {
    channel: {
      type: String,
      required: true,
      validator: (value) => ['whatsapp', 'googlercs', 'sms'].includes(value)
    },
    pageTitle: {
      type: String,
      default: 'Compliance keywords'
    },
    initialKeywordsData: {
      type: Array,
      default: () => []
    },
    messageJsonElements: {
      type: Array,
      default: () => []
    },
    carouselMessage: {
      type: Array,
      default: () => []
    },
    selectedTemplate: {
      type: Object,
      default: () => ({})
    },
    previewData: {
      type: Object,
      default: () => ({})
    },
    templatesLoading: {
      type: Boolean,
      default: false
    },
    finalUrlType: {
      type: String,
      default: 'static'
    },
    messageType: {
      type: String,
      default: 'none'
    },
    textMessage: {
      type: String,
      default: ''
    },
    quickReplyMessage: {
      type: String,
      default: ''
    },
    quickReplyButtons: {
      type: Array,
      default: () => []
    }
  },
  emits: [
    'refresh',
    'save-keywords', 
    'load-keywords',
    'keyword-added',
    'rule-selected',
    'fetch-rule',
    'final-url-type'
  ],
  data() {
    return {
      allComplianceKeywordsRule: [],
      selectedRule: null,
      currentStep: 'keywords_table',
      selectedAccordion: 'keywords',
      newKeyword: '',
    }
  },
  computed: {
    selectedAdMessageJSON() {
      return this.messageJsonElements || [];
    },
    selectedBroadCastChannel() {
      return this.channel;
    },
    displayRules() {
      const defaultRules = [
        { keywords: [], type: "Opt-in", active: true },
        { keywords: [], type: "Opt-out", active: true }
      ];

      if (this.allComplianceKeywordsRule.length === 0) {
        return defaultRules;
      }

      const hasOptIn = this.allComplianceKeywordsRule.some(rule => rule.type === 'Opt-in');
      const hasOptOut = this.allComplianceKeywordsRule.some(rule => rule.type === 'Opt-out');
      
      let rules = [...this.allComplianceKeywordsRule];
      
      if (!hasOptIn) rules.push(defaultRules[0]);
      if (!hasOptOut) rules.push(defaultRules[1]);
      
      return rules.sort((a, b) => a.type.localeCompare(b.type));
    }
  },
  watch: {
    initialKeywordsData: {
      handler(newData) {
        if (newData && newData.length > 0) {
          this.allComplianceKeywordsRule = [...newData];
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.initializeKeywords();
  },
  methods: {
    initializeKeywords() {
      useUIStore().updateLoading({ isLoading: true });
      this.$emit('load-keywords', this.channel);
    },
    async editComplianceKeyword(rule) {
      this.currentStep = 'editComplianceKeyword';
      this.selectedRule = { ...rule };
      
      if (this.channel === 'whatsapp') {
        this.$emit('rule-selected', this.selectedRule);
      }
      
      if (rule.id && this.channel !== 'whatsapp') {
        try {
          useUIStore().updateLoading({ isLoading: true });
          this.$emit('fetch-rule', rule.id);
        } catch (error) {
          console.error('Error fetching rule data:', error);
          useUIStore().updateLoading({ isLoading: false });
        }
      } else {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    updateSelectedRule(ruleData) {
      this.selectedRule = { ...ruleData };
      useUIStore().updateLoading({ isLoading: false });
    },
    accordionGroupChange(value) {
      this.selectedAccordion = this.selectedAccordion === value ? '' : value;
    },
    addNewKeyword() {
      if (!this.newKeyword.trim()) return;
      
      if (this.selectedRule.keywords.includes(this.newKeyword.trim())) {
        useUIStore().updateToast({ open: true, message: "Keyword already exists", type: "danger" });
        return;
      }

      this.selectedRule.keywords.push(this.newKeyword.trim());
      this.newKeyword = '';
    },
    deleteKeyword(keyword) {
      const index = this.selectedRule.keywords.indexOf(keyword);
      if (index > -1) {
        this.selectedRule.keywords.splice(index, 1);
      }
    },
    updateKeywordsData(data) {
      this.allComplianceKeywordsRule = [...data];
      useUIStore().updateLoading({ isLoading: false });
    },
    getKeywordsData() {
      return this.allComplianceKeywordsRule;
    },
    setLoading(state) {
      useUIStore().updateLoading({ isLoading: state });
    },
    stopLoading() {
      useUIStore().updateLoading({ isLoading: false });
    },
    resetToKeywordsTable() {
      this.currentStep = 'keywords_table';
      this.selectedRule = null;
    },
    handleUrlTypeUpdate(urlType) {
      this.$emit('final-url-type', urlType);
    },
    saveComplianceKeywords() {
      if (!this.selectedRule) return;
      this.$emit('keyword-added', {
        keywords: this.selectedRule.keywords,
        id: this.selectedRule?.id,
        type: this.selectedRule.type.toLowerCase().replace('-', '_'),
        description: "",
        active: this.selectedRule.active || true,
        channel: this.channel,
        message: {},
        url_types: {},
        template_data: {}
      });
      
      const ruleIndex = this.allComplianceKeywordsRule.findIndex(r => r.type === this.selectedRule.type);
      if (ruleIndex !== -1) {
        this.allComplianceKeywordsRule[ruleIndex] = { ...this.selectedRule };
      } else {
        this.allComplianceKeywordsRule.push({ ...this.selectedRule });
      }
    }
  }
}
</script>

<style scoped>
.model-sections {
  background: var(--white);
  border-radius: 5px;
}
.sectionPadding {
  padding: 15px 20px;
}

/* Toolbar header */
.toolbar-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 20px;
  background: var(--white);
  border-bottom: 1px solid var(--background-primary);
}
.save-btn {
  margin-left: auto;
}

/* Table layout */
.table-header,
.table-row {
  display: grid;
  grid-template-columns: 3fr 2fr 1fr;
  align-items: center;
  gap: 10px;
}
.text-center {
  text-align: center;
}
.text-end {
  text-align: right;
}

/* Keyword chips */
.bot-trigger-chip {
  background-color: #EDDEFF;
  display: inline-block;
  margin-right: 6px;
  margin-bottom: 6px;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 14px;
}

.th_keywords {
  display: inline-block;
  vertical-align: middle;
  padding-right: 5px;
}
.th_keywords_tooltip {
  display: inline-block;
  vertical-align: middle;
  padding-bottom: 3px;
}

/* Accordion */
.accordion-container {
  border-radius: 5px;
  background: var(--white);
  overflow: hidden;
}
.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: var(--white);
  border-bottom: 1px solid var(--background-primary);
}
.accordion-content {
  background: var(--white);
}

/* Keyword edit table */
.keyword-table-header,
.keyword-table-row {
  display: grid;
  grid-template-columns: 3fr 1fr;
  align-items: center;
  gap: 10px;
}

/* Form inputs */
.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}

/* Preview section */
.previewContainer {
  border-radius: 5px;
}
.previewHeader {
  border-bottom: 1px solid var(--background-primary);
  padding: 20px;
}
.no-preview-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 20px;
}
.preview-placeholder {
  text-align: center;
}
.preview-placeholder-icon {
  margin-bottom: 8px;
}

/* Info banner */
.infoBanner {
  background-color: var(--purple-opac);
  padding: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  border-radius: 5px;
}
.infoBanner .icon {
  width: 20px;
  height: 20px;
}
.infoBanner .column {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
}

.messagePadding {
  padding: 0px 20px 20px 20px;
}
.custPaddingRight {
  padding-right: 15px;
}

@media only screen and (max-width: 780px) {
  .custPaddingRight {
    padding-right: 0px;
  }
}
</style>
